import { Injectable } from '@angular/core';
import type { Row, Worksheet } from 'exceljs';
import type {
  CommissionRecoveryApplicationItem,
  CommissionRecoveryItem,
  CommissionRecoveryStatus,
} from './commission-recovery.service';

export interface CommissionRecoveryExportFilters {
  dateFrom: string;
  dateTo: string;
  status: string;
  searchTerm: string;
}

@Injectable({ providedIn: 'root' })
export class CommissionRecoveryExportService {
  async export(
    items: CommissionRecoveryItem[],
    filters: CommissionRecoveryExportFilters,
  ): Promise<void> {
    const excelJsModule = (await import('exceljs')) as unknown as {
      default: typeof import('exceljs');
    };
    const workbook = new excelJsModule.default.Workbook();
    workbook.creator = 'All To Trips';
    workbook.created = new Date();
    workbook.modified = new Date();

    const worksheet = workbook.addWorksheet('ยอดหักคืนค่าคอม', {
      properties: { defaultRowHeight: 20 },
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    });

    this.configureColumns(worksheet);
    this.addReportHeader(worksheet, filters, items.length);
    this.addTable(worksheet, items);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `ยอดหักคืนค่าคอม-${filters.dateFrom || 'all'}-${filters.dateTo || 'all'}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  private configureColumns(worksheet: Worksheet): void {
    const widths = [
      8, 16, 18, 28, 20, 34, 20, 22, 18, 18, 18, 18, 18, 18, 48,
    ];
    widths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });
  }

  private addReportHeader(
    worksheet: Worksheet,
    filters: CommissionRecoveryExportFilters,
    itemCount: number,
  ): void {
    worksheet.mergeCells('A1:O1');
    const title = worksheet.getCell('A1');
    title.value = 'รายงานยอดหักคืนค่าคอม';
    title.font = { bold: true, size: 16, color: { argb: 'FF0F172A' } };
    title.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 28;

    const filterRows: Array<[string, string | number, string, string | number]> = [
      ['วันที่เริ่มต้น', filters.dateFrom || 'ทั้งหมด', 'วันที่สิ้นสุด', filters.dateTo || 'ทั้งหมด'],
      ['สถานะ', filters.status, 'คำค้นหา', filters.searchTerm || 'ไม่ได้ระบุ'],
      ['จำนวนรายการ', itemCount, 'วันที่ออกรายงาน', new Intl.DateTimeFormat('th-TH').format(new Date())],
    ];

    filterRows.forEach(([leftLabel, leftValue, rightLabel, rightValue]) => {
      const row = worksheet.addRow([]);
      row.getCell(4).value = leftLabel;
      row.getCell(5).value = leftValue;
      row.getCell(9).value = rightLabel;
      row.getCell(10).value = rightValue;

      [4, 9].forEach((column) => {
        const cell = row.getCell(column);
        cell.font = { bold: true };
        cell.border = this.thinBorder();
      });
      [5, 10].forEach((column) => {
        row.getCell(column).border = this.thinBorder();
      });
    });
    worksheet.addRow([]);
  }

  private addTable(worksheet: Worksheet, items: CommissionRecoveryItem[]): void {
    const headerRow = worksheet.addRow([
      'ลำดับ',
      'วันที่',
      'รหัสพนักงาน',
      'ผู้รับคอม',
      'Booking ต้นทาง',
      'ชื่อทัวร์',
      'เลขที่คืนเงิน',
      'เลขที่คอมเดิม',
      'ประเภทผู้รับคอม',
      'คอมเดิม',
      'ยอดต้องหัก',
      'หักแล้ว',
      'คงเหลือ',
      'สถานะ',
      'ประวัติการหัก',
    ]);
    this.styleHeaderRow(headerRow, 15);
    const headerRowNumber = headerRow.number;

    items.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        new Date(item.createdAt),
        item.recipientUser.employeeCode,
        `${item.recipientUser.firstName} ${item.recipientUser.lastName}`.trim(),
        item.booking.bookingNo,
        item.booking.tourName,
        item.bookingRefund.refundNo,
        item.bookingCommission.commissionNo,
        item.bookingCommission.recipientRole === 'CARETAKER' ? 'ผู้ดูแลรายการ' : 'พนักงานขาย',
        Number(item.originalAmount),
        Number(item.recoveryAmount),
        Number(item.settledAmount),
        Number(item.remainingAmount),
        this.statusLabel(item.status),
        item.applications.map((application) => this.applicationSummary(application)).join('\n'),
      ]);
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = this.thinBorder();
        cell.alignment = { vertical: 'top', wrapText: true };
      });
      row.getCell(2).numFmt = 'dd/mm/yyyy';
      [10, 11, 12, 13].forEach((column) => {
        row.getCell(column).numFmt = '#,##0.00';
      });
    });

    const totalRow = worksheet.addRow([
      `รวมทั้งหมด (${items.length} รายการ)`,
      '', '', '', '', '', '', '', '',
      items.reduce((total, item) => total + Number(item.originalAmount), 0),
      items.reduce((total, item) => total + Number(item.recoveryAmount), 0),
      items.reduce((total, item) => total + Number(item.settledAmount), 0),
      items.reduce((total, item) => total + Number(item.remainingAmount), 0),
      '', '',
    ]);
    totalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.border = this.thinBorder();
    });
    [10, 11, 12, 13].forEach((column) => {
      totalRow.getCell(column).numFmt = '#,##0.00';
    });

    worksheet.autoFilter = {
      from: { row: headerRowNumber, column: 1 },
      to: { row: headerRowNumber, column: 15 },
    };
    worksheet.views = [{ state: 'frozen', ySplit: headerRowNumber }];
  }

  private applicationSummary(application: CommissionRecoveryApplicationItem): string {
    const targetCode =
      application.bookingCommission?.commissionNo ?? application.tourCommission?.commissionNo ?? '-';
    const status = {
      RESERVED: 'กันยอดไว้',
      APPLIED: 'หักคืนแล้ว',
      RELEASED: 'คืนยอดกลับ',
    }[application.status];
    return `${targetCode} · ${status} · ${Number(application.amount).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} บาท`;
  }

  private statusLabel(status: CommissionRecoveryStatus): string {
    return {
      PENDING: 'รอหักคืน',
      PARTIAL: 'หักคืนบางส่วน',
      SETTLED: 'หักคืนครบแล้ว',
      WAIVED: 'ยกเว้นยอด',
      CANCELLED: 'ยกเลิก',
    }[status];
  }

  private styleHeaderRow(row: Row, columnCount: number): void {
    row.height = 30;
    for (let column = 1; column <= columnCount; column += 1) {
      const cell = row.getCell(column);
      cell.font = { bold: true, color: { argb: 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = this.thinBorder();
    }
  }

  private thinBorder() {
    const side = { style: 'thin' as const, color: { argb: 'FFE2E8F0' } };
    return { top: side, left: side, bottom: side, right: side };
  }
}
